/*
 * Copyright (c) 2021 CalAmp Corp.  All Rights Reserved
 */
import React from 'react';
import {translate} from '@ion/core/src/locale/locale';
import {GroupCategoryEntity} from '@ion/core/src/types/entities';
import EntityPropertyChooser, {
  EntityPropertyChooserClientProps,
  GroupPropertyKind
} from './EntityPropertyChooser';

// extended composed props and expose them as a convenience to the client ...
export interface LandmarkCategoryChooserProps extends EntityPropertyChooserClientProps {
  entity: GroupCategoryEntity
};

/**
 * @LandmarkCategoryChooser
 *
 * The LandmarkCategoryChooser provides a list of user defined, account specific entity
 * landmark categories for display within a dropdown list, allowing the user to select a category
 * from the list for subsequent persistence to the entity.
 *
 * ```typescript
 *
 *     import {LandmarkCategoryChooser} from '@ion/dls/src/editors/';
 *     import {GroupCategoryEntity} from '@ion/core/src/types/entities';
 *
 *     // add an entity landmark category chooser for notifications ...
 *     <LandmarkCategoryChooser entity={GroupCategoryEntity.landmark}
 *       defaultValue={notifications.landmark}
 *       isLoading={isLoading}
 *       onChange(setLandmark) />
 *
 *
 *     // add a read-only entity landmark category chooser for notifications...
 *     <LandmarkCategoryChooser entity={GroupCategoryEntity.landmark}
 *       isLoading={isLoading}
 *       defaultValue={notifications.landmark}
 *       readOnly />
 *
 * ```
 *
 * @entity            Required, specifies the context in which the component is displaying
 *                    entity categories (e.g. "landmark Categories", "Route Categories").
 * @isLoading         Required, when true, indicates that the component is to display its
 *                    loading state, and when changed to false, indicates that the items
 *                    within the dropdown are to be updated to reflect the latest entity
 *                    categories from the GroupProperties API.
 * @defaultValue      Allows for providing a default selected value from the list.
 * @readOnly          When true, the component will be displayed in read-only mode;
 *                    that is, disabled. Defaults to false.
 * @onChange          Callback prop which will be invoked with the selected entity category.
 * @values            Optional list of entity categories to be used. When provided, the
 *                    GroupProperties API will be bypassed and only the items provided
 *                    will be displayed.
 * @includeGroupName  When true, indicates that the account for which the entity categories
 *                    have been provided should be displayed within the label.
 * @classes           Optional classes to apply to the wrapping element.
 */

export const LandmarkCategoryChooser = (props: EntityPropertyChooserClientProps) => {
  function labelFormatter(label: string, name: string) {
    return (name.toLowerCase() === GroupCategoryEntity.landmark
      ? `${translate('common:entityLabels.geofence')} ${label.replace(name, '')}`
      : label);
  };

  return (
    <EntityPropertyChooser kind={GroupPropertyKind.Categories}
      labelFormatter={labelFormatter}
      translation='common:editors.entityCategory'
      {...props} />);
};
export default LandmarkCategoryChooser;
